/*
 *  The tier ladder — Bronze, Silver, Gold, Platinum, Diamond.
 *
 *  Thresholds live in `app/services/leaderboard.py` and arrive on the wire, so
 *  the numbers are never duplicated here; this module owns only the look.
 *
 *  Absolute thresholds, not percentiles: a whole team can climb in the same
 *  month. That is the difference between a ladder and a ranking, and it is why
 *  the tier sits next to the rank rather than replacing it.
 */
import ironPng from "@/assets/tiers/iron.png";
import bronzePng from "@/assets/tiers/bronze.png";
import silverPng from "@/assets/tiers/silver.png";
import goldPng from "@/assets/tiers/gold.png";
import platinumPng from "@/assets/tiers/platinum.png";
import emeraldPng from "@/assets/tiers/emerald.png";
import diamondPng from "@/assets/tiers/diamond.png";
import masterPng from "@/assets/tiers/master.png";
import grandmasterPng from "@/assets/tiers/grandmaster.png";
import challengerPng from "@/assets/tiers/challenger.png";
import ironLgPng from "@/assets/tiers/lg/iron.png";
import bronzeLgPng from "@/assets/tiers/lg/bronze.png";
import silverLgPng from "@/assets/tiers/lg/silver.png";
import goldLgPng from "@/assets/tiers/lg/gold.png";
import platinumLgPng from "@/assets/tiers/lg/platinum.png";
import emeraldLgPng from "@/assets/tiers/lg/emerald.png";
import diamondLgPng from "@/assets/tiers/lg/diamond.png";
import masterLgPng from "@/assets/tiers/lg/master.png";
import grandmasterLgPng from "@/assets/tiers/lg/grandmaster.png";
import challengerLgPng from "@/assets/tiers/lg/challenger.png";

/** Rank emblems, the artist's originals downscaled by
 *  `scripts/resize_tier_art.py`. Unranked has none on purpose — it is the
 *  absence of a rank, and drawing it an emblem would make it look like one. */
const TIER_ART: Record<string, string> = {
  iron: ironPng,
  bronze: bronzePng,
  silver: silverPng,
  gold: goldPng,
  platinum: platinumPng,
  emerald: emeraldPng,
  diamond: diamondPng,
  master: masterPng,
  grandmaster: grandmasterPng,
  challenger: challengerPng,
};

/** The same emblems at 256px, for the places they are drawn large enough that
 *  the detail matters. Kept a separate set rather than serving one big file
 *  everywhere: a board row can show ten different tiers at 15px, and paying
 *  ~50 KB each for pixels thrown away in the downscale is the whole cost of
 *  the feature. Only the viewer's own tier ever loads from here. */
const TIER_ART_LG: Record<string, string> = {
  iron: ironLgPng,
  bronze: bronzeLgPng,
  silver: silverLgPng,
  gold: goldLgPng,
  platinum: platinumLgPng,
  emerald: emeraldLgPng,
  diamond: diamondLgPng,
  master: masterLgPng,
  grandmaster: grandmasterLgPng,
  challenger: challengerLgPng,
};

/** Emblem sizes. Deliberately larger than the glyph they replace: at the old
 *  11px these are unreadable mush, and the artwork is the point. */
const ART_PX: Record<string, number> = {
  xs: 15,
  sm: 18,
  bare: 16,
  ladder: 22,
  hero: 96,
};

/** The 64px set covers anything drawn at 32 CSS px or less even on a 2x
 *  display; past that it would visibly soften, so the hero set takes over. */
function srcFor(tier: string, px: number): string | undefined {
  return (px > 32 ? TIER_ART_LG[tier] : TIER_ART[tier]) ?? undefined;
}

export function TierArt({
  tier,
  px,
  plate = false,
  className = "",
}: {
  tier: string;
  px: number;
  /** Sit the emblem on a faint tinted disc. Iron and Bronze are near-black
   *  charcoal and brown, which all but vanish against the dark theme's
   *  `#16161b` card; the plate lifts them without repainting the artwork.
   *  Off for inline chips, where a disc behind a 15px emblem is just noise. */
  plate?: boolean;
  className?: string;
}) {
  const src = srcFor(tier, px);
  const look = tierLook(tier);
  const art = src ? (
    <img
      src={src}
      alt=""
      aria-hidden
      width={px}
      height={px}
      className={`inline-block flex-shrink-0 select-none ${className}`}
      style={{ width: px, height: px, objectFit: "contain" }}
    />
  ) : (
    // Unranked — a hollow marker, never an emblem.
    <span
      className={`leading-none ${look.text} ${className}`}
      style={{ fontSize: px * 0.8 }}
      aria-hidden
    >
      ◇
    </span>
  );
  if (!plate) return art;
  const box = Math.round(px * 1.3);
  return (
    <span
      className="tier-plate inline-flex items-center justify-center flex-shrink-0 rounded-[28%]"
      style={{ width: box, height: box }}
    >
      {art}
    </span>
  );
}

/** The artwork at a size where it can actually be read — the one placement
 *  that justifies shipping it. */
export function TierEmblem({
  tier,
  px = ART_PX.hero,
  className = "",
}: {
  tier: string;
  px?: number;
  className?: string;
}) {
  return <TierArt tier={tier} px={px} plate className={className} />;
}

export interface TierStep {
  key: string;
  label: string;
  floor: number;
}

interface TierLook {
  label: string;
  text: string;
  chip: string;
  bar: string;
}

/*  Tiers deliberately do NOT use 🥇🥈🥉 — those are the podium's rank medals,
 *  and reusing them put a gold tier emblem next to a bronze rank medal on the
 *  same row. The artwork carries the tier and the medal carries the rank, so
 *  the two stay visually separate.  */
export const TIER_META: Record<string, TierLook> = {
  // Not a tier — the absence of one. Deliberately the quietest thing on the
  // ladder: it marks "not started" rather than "last place".
  unranked: {
    label: "Unranked",
    text: "text-slate-600",
    chip: "border-white/10 bg-white/[0.03] text-slate-500",
    bar: "bg-slate-600",
  },
  iron: {
    label: "Iron",
    text: "text-slate-500",
    chip: "border-slate-500/30 bg-slate-500/10 text-slate-400",
    bar: "bg-slate-500",
  },
  bronze: {
    label: "Bronze",
    text: "text-amber-700",
    chip: "border-amber-700/30 bg-amber-700/10 text-amber-600",
    bar: "bg-amber-700",
  },
  silver: {
    label: "Silver",
    text: "text-slate-300",
    chip: "border-slate-400/30 bg-slate-400/10 text-slate-300",
    bar: "bg-slate-400",
  },
  gold: {
    label: "Gold",
    text: "text-yellow-400",
    chip: "border-yellow-400/30 bg-yellow-400/10 text-yellow-400",
    bar: "bg-yellow-400",
  },
  platinum: {
    label: "Platinum",
    text: "text-sky-300",
    chip: "border-sky-400/30 bg-sky-400/10 text-sky-300",
    bar: "bg-sky-400",
  },
  emerald: {
    label: "Emerald",
    text: "text-emerald-400",
    chip: "border-emerald-400/30 bg-emerald-400/10 text-emerald-400",
    bar: "bg-emerald-400",
  },
  diamond: {
    label: "Diamond",
    text: "text-cyan-200",
    chip: "border-cyan-300/30 bg-cyan-300/10 text-cyan-200",
    bar: "bg-cyan-300",
  },
  master: {
    label: "Master",
    text: "text-violet-400",
    chip: "border-violet-400/30 bg-violet-400/10 text-violet-400",
    bar: "bg-violet-400",
  },
  grandmaster: {
    label: "Grandmaster",
    text: "text-rose-400",
    chip: "border-rose-400/30 bg-rose-400/10 text-rose-400",
    bar: "bg-rose-400",
  },
  challenger: {
    label: "Challenger",
    text: "text-fuchsia-300",
    chip: "border-fuchsia-400/40 bg-fuchsia-400/15 text-fuchsia-300",
    bar: "bg-fuchsia-400",
  },
};

export function tierLook(key: string | undefined): TierLook {
  return TIER_META[key ?? "unranked"] ?? TIER_META.unranked;
}

/** Name plus emblem. `size="xs"` is the inline form used inside table rows. */
export function TierChip({
  tier,
  size = "sm",
  bare = false,
}: {
  tier: string;
  size?: "xs" | "sm";
  bare?: boolean;
}) {
  const t = tierLook(tier);
  if (bare) {
    return (
      <span className="inline-flex items-center" title={`${t.label} tier`}>
        <TierArt tier={tier} px={ART_PX.bare} />
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border pl-1 pr-1.5 py-0.5 font-medium ${
        t.chip
      } ${size === "xs" ? "text-[10px]" : "text-[11px]"}`}
    >
      <TierArt tier={tier} px={size === "xs" ? ART_PX.xs : ART_PX.sm} />
      {t.label}
    </span>
  );
}

/** Where you are on the ladder and what the next rung costs.
 *
 *  The bar fills *within the current tier* rather than across the whole
 *  ladder: the actionable number is the distance to the next rung, and a bar
 *  measured against Diamond would sit near-empty all month for most brokers.
 */
export function TierProgress({
  tier,
  score,
  steps,
  toNext,
  nextTier,
  compact = false,
}: {
  tier: string;
  score: number;
  steps: TierStep[];
  toNext: number | null;
  nextTier: string | null;
  compact?: boolean;
}) {
  const t = tierLook(tier);
  const i = steps.findIndex((s) => s.key === tier);
  const floor = i >= 0 ? steps[i].floor : 0;
  const ceil = i >= 0 && i + 1 < steps.length ? steps[i + 1].floor : null;
  // Clamped: a negative score (removals outstripping credits) must not render
  // as a negative width.
  const pct =
    ceil == null
      ? 100
      : Math.max(0, Math.min(100, ((score - floor) / (ceil - floor)) * 100));

  const nextLine =
    toNext != null && nextTier ? (
      <>
        {toNext.toLocaleString()} to {tierLook(nextTier).label}
      </>
    ) : (
      "Top of the ladder"
    );
  const bar = (
    <div
      className={`flex h-1.5 rounded-full overflow-hidden bg-white/[0.06] ${
        compact ? "mt-1.5" : ""
      }`}
    >
      <div className={t.bar} style={{ width: `${pct}%` }} />
    </div>
  );

  // Compact is Home's form and stays a single row: the comment in RankPanel
  // records that two extra stacked rows already pushed that page past its
  // one-screen budget once.
  if (compact) {
    return (
      <div>
        <div className="flex items-center gap-2">
          <TierChip tier={tier} size="xs" />
          <span className="ml-auto text-[10.5px] text-slate-500 truncate">
            {nextLine}
          </span>
        </div>
        {bar}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <TierEmblem tier={tier} />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-baseline gap-2">
          <span className={`text-[15px] font-semibold leading-none ${t.text}`}>
            {t.label}
          </span>
          <span className="text-[11px] tabular-nums text-slate-500">
            {score.toLocaleString()} pts
          </span>
        </div>
        <div className="text-[10.5px] text-slate-500 truncate">{nextLine}</div>
        {bar}
      </div>
    </div>
  );
}

/** The whole ladder with every rung's threshold — the "how do I get there"
 *  reference. Rungs already reached are lit. */
export function TierLadder({ steps, tier }: { steps: TierStep[]; tier: string }) {
  const at = steps.findIndex((s) => s.key === tier);
  return (
    <div className="divide-y divide-white/[0.04]">
      {steps.map((s, i) => {
        const look = tierLook(s.key);
        const reached = i <= at;
        return (
          <div
            key={s.key}
            className={`flex items-center gap-2.5 px-4 py-1.5 ${
              reached ? "" : "opacity-45"
            } ${i === at ? "bg-white/[0.03]" : ""}`}
          >
            <TierArt
              tier={s.key}
              px={ART_PX.ladder}
              plate
              // Locked rungs read as grey rather than washed out: at 35%
              // opacity on a white card they vanished, and you cannot aim at
              // something you cannot see.
              className={reached ? "" : "grayscale opacity-70"}
            />
            <span className={`text-[12px] ${reached ? look.text : "text-slate-400"}`}>
              {look.label}
            </span>
            {i === at && (
              <span className="text-[10px] text-slate-500">you're here</span>
            )}
            <span className="ml-auto text-[11px] tabular-nums text-slate-500">
              {s.key === "unranked" ? "under 300" : `${s.floor.toLocaleString()}+`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Avatar ring
 * ------------------------------------------------------------------ */

/** Ring colour per tier. Bronze is absent on purpose: the API never emits it,
 *  because a bronze ring in every list would publish the bottom of the board
 *  and hang a permanent "you are last" marker on the person who least needs
 *  one. The ring marks achievement only, so its absence stays ambiguous. */
const RING: Record<string, string> = {
  gold: "ring-yellow-400/80",
  platinum: "ring-sky-400/80",
  emerald: "ring-emerald-400/80",
  diamond: "ring-cyan-300/90",
  master: "ring-violet-400/90",
  grandmaster: "ring-rose-400/90",
  challenger: "ring-fuchsia-400",
};

/** Tailwind ring width by avatar size — a 2px ring on a 24px avatar reads as
 *  a smudge, and a 1.5px ring on a 64px one disappears. */
const RING_W: Record<string, string> = {
  xs: "ring-[1.5px]",
  sm: "ring-2",
  md: "ring-2",
  lg: "ring-[3px]",
};

export function tierRingClass(
  tier: string | null | undefined,
  size: "xs" | "sm" | "md" | "lg" = "md",
): string {
  const c = tier ? RING[tier] : undefined;
  return c ? `ring ${RING_W[size]} ${c}` : "";
}
